import { DateTimePickerComponent } from "@syncfusion/ej2-react-calendars";
import {
  Divider, ErrorMessages, IconButton, IconButtonType, Page, Spinner
} from "@whitespace/whitespace-ui";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { formatDecimalToHoursAndMinutes } from "helpers/DateUtils";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import ICollector from "interfaces/ICollector";
import IDisposalSite from "interfaces/IDisposalSite";
import ISlaDetails from "interfaces/ISlaDetails";
import IWorkOrderViewModel from "interfaces/IWorkOrderViewModel";
import IModifyWorkOrderRequestModel from "interfaces/Request/IModifyWorkOrderRequestModel";
import { useStateMachine } from "little-state-machine";
import { makeDateTimePickerAACompliant } from "modules/SyncfusionAAFixer";
import { useState, useEffect, useCallback } from "react";
import {
  Breadcrumb, Button, Card, Col, Form, Row
} from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import IGetWorkOrderResponseModel from "interfaces/Response/IGetWorkOrderResponseModel";
import { AllowedAllocateToStatuses, WorkOrderStatusEnum } from "modules/WorkOrderStatusEnum";
import { mapWorkOrderResponseToWorkOrderViewModel } from "../ViewWorkOrder/DataMappers/ViewWorkOrderMapper";
import ModifyWorkOrderBreadcrumb from "./ModifyWorkOrderBreadcrumb";

const ModifyWorkOrder = () => {
  const navigate = useNavigate();
  const { state } = useStateMachine();
  const {
    formState: { errors },
    register,
    handleSubmit,
    getValues,
    setValue,
    watch
  } = useForm<IModifyWorkOrderRequestModel>({
    shouldFocusError: true,
    reValidateMode: "onBlur"
  });

  const {
    id, source, sourceType, sourceId
  } = useParams();

  const [slaStart, setSlaStart] = useState<Date>();
  const [slaStartMin, setSlaStartMin] = useState<Date>();
  const [workOrder, setWorkOrder] = useState<IWorkOrderViewModel>();
  const [showExceptions, setShowExceptions] = useState<boolean>(true);
  const [modifyWorkOrderButtonDisabled, setModifyWorkOrderButtonDisabled] = useState<boolean>(true);
  const [isWorkOrderTempDropped, setIsWorkOrderTempDropped] = useState<boolean>(true);
  const [isAllocateToDisabled, setIsAllocateToDisabled] = useState<boolean>(true);

  const {
    isSending: getWorkOrderIsSending, errors: getWorkOrderErrors, data: getWorkOrderData, sendRequest: getWorkOrderSendRequest
  } = useAmplifyREST();

  const {
    isSending: getCollectorsIsSending, errors: getCollectorsErrors, data: getCollectorsData, sendRequest: getCollectorsSendRequest
  } = useAmplifyREST();

  const {
    isSending: getSlaDetailsIsSending, errors: getSlaDetailsErrors, data: getSlaDetailsData, sendRequest: getSlaDetailsSendRequest
  } = useAmplifyREST();

  const {
    isSending: getDisposalSitesIsSending, errors: getDisposalSitesErrors, data: getDisposalSitesData, sendRequest: getDisposalSitesSendRequest
  } = useAmplifyREST();

  const {
    isSending: modifyWorkOrderIsSending, errors: modifyWorkOrderErrors, statusCode: modifyWorkOrderStatusCode, sendRequest: modifyWorkOrderSendRequest
  } = useAmplifyREST();

  const isSending = getWorkOrderIsSending || getCollectorsIsSending || getSlaDetailsIsSending || getDisposalSitesIsSending || modifyWorkOrderIsSending;
  const apiErrors = getWorkOrderErrors || getCollectorsErrors || getSlaDetailsErrors || getDisposalSitesErrors || modifyWorkOrderErrors;
  const [initialApiLoadComplete, setInitialLoadComplete] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      getWorkOrderSendRequest(`workorders/${encodeURI(id!)}`, "GET");
    }
  }, [getWorkOrderSendRequest, id]);

  useEffect(() => {
    if (getWorkOrderData) {
      const workOrderViewModel = mapWorkOrderResponseToWorkOrderViewModel((getWorkOrderData as IGetWorkOrderResponseModel).workOrder);
      setWorkOrder(workOrderViewModel);
      setIsWorkOrderTempDropped(workOrderViewModel.status.localeCompare(WorkOrderStatusEnum.TempDropped, undefined, { sensitivity: "accent" }) === 0);
      setIsAllocateToDisabled(!AllowedAllocateToStatuses.includes(workOrderViewModel.status));
    }
  }, [getWorkOrderData]);

  useEffect(() => {
    if (workOrder) {
      getCollectorsSendRequest(`regions/${workOrder!.regionId}/collectors/active`, "GET");
    }
  }, [getCollectorsSendRequest, workOrder]);

  useEffect(() => {
    getSlaDetailsSendRequest("settings/sla/details", "GET");
  }, [getSlaDetailsSendRequest]);

  useEffect(() => {
    if (workOrder) {
      getDisposalSitesSendRequest(`regions/${workOrder!.regionId}/disposalsites/active`, "GET");
    }
  }, [getDisposalSitesSendRequest, workOrder]);

  useEffect(() => {
    if (apiErrors || (workOrder && getCollectorsData && getSlaDetailsData && getDisposalSitesData && isWorkOrderTempDropped !== undefined)) {
      setInitialLoadComplete(true);
    }
  }, [workOrder, getCollectorsData, getSlaDetailsData, getDisposalSitesData, isWorkOrderTempDropped, apiErrors]);

  const renderCollectors = () => {
    const collectors = [
      <option key="" value="">Please Select</option>
    ];
    if (getCollectorsData) {
      (getCollectorsData.collectors as ICollector[]).forEach(collector => {
        collectors.push(<option key={collector.id} value={collector.id}>{`${collector.name} - ${collector.type}`}</option>);
      });
    }

    return collectors;
  };

  const renderSlaTimes = () => {
    const slaTimes :any = [];

    if (getSlaDetailsData) {
      (getSlaDetailsData.slaDetails as ISlaDetails).slaTimes.forEach(slaTime => {
        slaTimes.push(<option key={`slaTime_${slaTime}`} value={slaTime}>{formatDecimalToHoursAndMinutes(slaTime)}</option>);
      });
    }

    return slaTimes;
  };

  const renderDisposalSites = () => {
    const disposalSites = [
      <option key="" value="">Please Select</option>
    ];
    if (getDisposalSitesData) {
      (getDisposalSitesData.disposalSites as IDisposalSite[]).forEach(disposalSite => {
        disposalSites.push(<option key={disposalSite.id} value={disposalSite.id}>{disposalSite.name}</option>);
      });
    }

    return disposalSites;
  };

  const watchFields = watch([
    "collectorId",
    "slaTime",
    "disposalSiteId",
    "reason"
  ]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  const canFormBeSubmitted = () => {
    const collectorId = isAllocateToDisabled ? workOrder?.collectorId : getValues("collectorId");

    const disposalSiteId = getValues("disposalSiteId");
    const slaTime = getValues("slaTime");
    const reason = getValues("reason");

    if (!collectorId || !getCollectorsData || !disposalSiteId) {
      return;
    }

    const disposalSiteRequired = (getCollectorsData.collectors as ICollector[]).find(collector => collector.id === collectorId)?.isVehicle === true;

    const requiredFieldsEntered = collectorId.length > 0
    && slaStart
    && Number(slaTime) !== 0
    && (!disposalSiteRequired || disposalSiteId.length > 0);

    const atLeastOneFieldChangedValue : boolean = (
      (collectorId.length > 0 && collectorId !== workOrder?.collectorId)
      || (disposalSiteId.length > 0 && disposalSiteId !== workOrder?.disposalSiteId)
      || slaStart !== workOrder?.slaStart
      || Number(slaTime) !== workOrder?.slaTime
      || reason !== workOrder?.updateReason
    );

    setModifyWorkOrderButtonDisabled(!(requiredFieldsEntered && atLeastOneFieldChangedValue));
  };

  useEffect(() => {
    canFormBeSubmitted();
  }, [watchFields]);

  useEffect(() => {
    canFormBeSubmitted();
  }, [slaStart]);

  useEffect(() => {
    const slaTimeMin = new Date(Date.now());
    slaTimeMin.setMinutes(slaTimeMin.getMinutes() <= 29 ? 0 : 30);
    setSlaStartMin(slaTimeMin);
  }, []);

  const handleModifyWorkOrderClick = (request : IModifyWorkOrderRequestModel) => {
    setShowExceptions(true);
    request.slaStart = slaStart!.toISOString();
    request.collectorId = isAllocateToDisabled ? workOrder?.collectorId! : request.collectorId;
    modifyWorkOrderSendRequest(`workorders/${encodeURI(id!)}`, "PUT", request);
  };

  useEffect(() => {
    if (modifyWorkOrderStatusCode === 200) {
      switch (state.viewWorkOrderPageOrigination) {
        case RoutePathsEnum.Map:
          navigate(`${state.viewWorkOrderPageOrigination}/${source}/${sourceType}/${sourceId}`);
          break;
        case RoutePathsEnum.WorkOrders:
        case RoutePathsEnum.Containers:
        default:
          navigate(`${RoutePathsEnum.WorkOrderView}/${id}`);
          break;
      }
    }
  }, [modifyWorkOrderStatusCode]);

  useEffect(() => {
    if (workOrder && setValue) {
      setValue("reason", workOrder.updateReason!);

      if (!isWorkOrderTempDropped) {
        setSlaStart(workOrder!.slaStart);
      }
    }
  }, [workOrder, setValue, isWorkOrderTempDropped]);

  useEffect(() => {
    if (initialApiLoadComplete) {
      makeDateTimePickerAACompliant();
    }
  }, [initialApiLoadComplete]);

  const navigateOnCancel = (): void => {
    switch (state.viewWorkOrderPageOrigination) {
      case RoutePathsEnum.Map:
        navigate(`${RoutePathsEnum.WorkOrderView}/${id}/${source}/${sourceType}/${sourceId}`);
        break;
      case RoutePathsEnum.WorkOrders:
      case RoutePathsEnum.Containers:
      default:
        navigate(`${RoutePathsEnum.WorkOrderView}/${id}`);
        break;
    }
  };

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Divider />
      <Card>
        <Card.Header>
          <h2>
            Modify Work Order
            { !initialApiLoadComplete && (
              <Spinner
                animation="grow"
                visible
                spinnerText="Loading"
                variant="info"
              />
            ) }

          </h2>
        </Card.Header>
        <Form autoComplete="off" onSubmit={handleSubmit(handleModifyWorkOrderClick)}>
          <Card.Body className="no-padding">
            <Row className="d-lg-flex w-100 align-items-center top-margin-small bottom-margin-small">
              <Col xs={12} lg={8} className="d-flex">
                {workOrder && <ModifyWorkOrderBreadcrumb />}
              </Col>
              <Col className="d-lg-flex justify-content-end user-filter">
                <IconButton
                  iconType={IconButtonType.Save}
                  variant="primary"
                  className="right-margin-small"
                  disabled={modifyWorkOrderButtonDisabled || isSending}
                  aria-disabled={modifyWorkOrderButtonDisabled || isSending}
                  aria-label="Modify work order"
                  type="submit"
                  spin={modifyWorkOrderIsSending}
                  id="btnModifyWorkflow"
                  data-testid="buttonModifyWorkOrder"
                >
                  Modify
                </IconButton>
                <Button
                  variant="secondary"
                  onClick={navigateOnCancel}
                  data-testid="buttonCancel"
                  aria-label={`Cancel modify work order and back to ${state.viewWorkOrderPageOrigination}`}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
            <Divider />
            {initialApiLoadComplete && workOrder
            && (
              <section className="modify-work-order pt-2">
                <Row className="mt-1 ms-1">
                  <Col>
                    <div className="control-pane">
                      <div className="control-section">

                        <div className="d-table w-100 horiz-form-label form-label">
                          <div className="d-table-row" data-testid="wokrOrderReference">
                            <div className="d-table-cell pb-2">Ref:</div>
                            <div className="d-table-cell pb-2">{workOrder!.workOrderReference}</div>
                          </div>
                          <div className="d-table-row" data-testid="hwrcSiteName">
                            <div className="d-table-cell pb-2">Site:</div>
                            <div className="d-table-cell pb-2">{`${workOrder!.hwrcSiteName} - ${workOrder!.regionName}`}</div>
                          </div>
                          {workOrder.tempDropHwrcSiteName
                          && (
                            <div className="d-table-row" data-testid="tempDropLocation">
                              <div className="d-table-cell pb-2">Temp Drop Location:</div>
                              <div className="d-table-cell pb-2">
                                {workOrder!.tempDropHwrcSiteName}
                                {" "}
                                {workOrder!.tempDropRegionName ? `- ${workOrder!.tempDropRegionName}` : ""}
                              </div>
                            </div>
                          )}
                          <div className="d-table-row" data-testid="containerType">
                            <div className="d-table-cell pb-2">Container Type:</div>
                            <div className="d-table-cell pb-2">{workOrder!.containerWasteType}</div>
                          </div>
                        </div>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="collectorId">
                            <Form.Label id="collectorLabel" className="horiz-form-label">
                              Allocate To:
                            </Form.Label>
                            <Col>
                              <Form.Select
                                {...register("collectorId", { onChange: () => canFormBeSubmitted() })}
                                data-testid="ddlCollectors"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="collectorLabel"
                                defaultValue={isWorkOrderTempDropped ? "" : workOrder.collectorId}
                                disabled={isAllocateToDisabled}
                              >
                                {renderCollectors()}
                              </Form.Select>
                            </Col>
                            <ErrorMessages fieldName="collectorId" errors={errors} validationFailures={null} />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="slaStart">
                            <Form.Label id="slaStartLabel" className="horiz-form-label">
                              SLA Start:
                            </Form.Label>
                            <Col>
                              <DateTimePickerComponent
                                data-testid="slaStart"
                                id="slaStart"
                                min={slaStartMin}
                                format="dd/MM/yyyy HH:mm"
                                timeFormat="HH:mm"
                                value={isWorkOrderTempDropped ? undefined : workOrder.slaStart!}
                                change={(dtp:any) => { setSlaStart(dtp.value); }}
                                htmlAttributes={{ "aria-labelledby": "slaStartLabel" }}
                              />

                            </Col>
                            <ErrorMessages fieldName="slaStart" errors={errors} validationFailures={null} />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="slaTime">
                            <Form.Label id="slaTimeLabel" className="horiz-form-label">
                              Set SLA:
                            </Form.Label>
                            <Col>
                              <Form.Select
                                {...register("slaTime", { onChange: () => canFormBeSubmitted() })}
                                data-testid="ddlSlaTimes"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="slaTimeLabel"
                                defaultValue={isWorkOrderTempDropped ? "" : workOrder.slaTime.toString()}
                              >
                                {renderSlaTimes()}

                              </Form.Select>

                            </Col>
                            <ErrorMessages fieldName="slaTime" errors={errors} validationFailures={null} />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="disposalSiteId">
                            <Form.Label id="disposalSiteLabel" className="horiz-form-label">
                              Disposal Site:
                            </Form.Label>
                            <Col>
                              <Form.Select
                                {...register("disposalSiteId", { onChange: () => canFormBeSubmitted() })}
                                data-testid="ddlDisposalSites"
                                aria-required
                                aria-invalid={false}
                                aria-labelledby="disposalSiteLabel"
                                defaultValue={isWorkOrderTempDropped ? "" : workOrder.disposalSiteId}
                              >
                                {renderDisposalSites()}
                              </Form.Select>
                            </Col>
                            <ErrorMessages fieldName="disposalSiteId" errors={errors} validationFailures={null} />
                          </Form.Group>
                        </Row>
                        <Row className="mb-2">
                          <Form.Group as={Row} controlId="reason">
                            <Form.Label
                              id="reasonLabel"
                              className="horiz-form-label"
                            >
                              Reason:
                            </Form.Label>
                            <Col>
                              <Form.Control
                                type="text"
                                role="textbox"
                                aria-required="false"
                                aria-invalid={false}
                                aria-labelledby="reasonLabel"
                                maxLength={50}
                                {...register("reason")}
                              />
                            </Col>
                            <ErrorMessages
                              fieldName="reason"
                              errors={errors}
                              validationFailures={null}
                            />
                          </Form.Group>
                        </Row>
                      </div>
                    </div>
                  </Col>
                </Row>
              </section>
            ) }
          </Card.Body>
        </Form>
      </Card>
    </Page>
  );
};

export default ModifyWorkOrder;
